/* iter563 — Starter Tiles Grid (iter565: smart urgency cues).
 *
 * Six large action tiles under the AdaptiveHeroCard. Designed for novices
 * who don't know where to start. Each tile is a single clear task with
 * empathetic Bulgarian copy.
 *
 * iter565 — Smart urgency: tiles that represent uncompleted DAILY tasks get
 * a gentle amber left border. In the evening (≥18h), incomplete tiles
 * (breakfast not logged, no weight in last 7 days, no sleep last night)
 * escalate to critical (rose + pulse) — a soft "не забравяй" nudge.
 *
 * Grid: 3×2 on desktop, 2×3 on tablet, 1×6 on mobile.
 * Tile surface is fully clickable (not just the button) for big tap targets.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import {
  Coffee,
  Scale,
  Droplet,
  Moon,
  Microscope,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import UrgencyBorder from "@/components/common/UrgencyBorder";

const TILES = [
  {
    id: "breakfast",
    icon: Coffee,
    title: "Запиши закуска",
    subtitle: "Кажи ни как започна денят ти.",
    cta: "Добавям",
    path: "/dashboard/diary",
    tint: "amber",
  },
  {
    id: "weight",
    icon: Scale,
    title: "Тегло днес",
    subtitle: "Следим промените стъпка по стъпка.",
    cta: "Въвеждам",
    path: "/dashboard/profile",
    tint: "violet",
  },
  {
    id: "water",
    icon: Droplet,
    title: "Чаша вода",
    subtitle: "Хидратацията има значение. Добави чаша.",
    cta: "+1 чаша",
    path: "/dashboard/diary",
    tint: "sky",
  },
  {
    id: "sleep",
    icon: Moon,
    title: "Колко спа?",
    subtitle: "Сънят влияе на глада, енергията и метаболизма.",
    cta: "Записвам",
    path: "/dashboard/analyses?tab=clinical",
    tint: "indigo",
  },
  {
    id: "labs",
    icon: Microscope,
    title: "Качете изследване",
    subtitle: "Снимай резултатите. Ще ги анализираме вместо теб.",
    cta: "Качвам",
    path: "/dashboard/analyses",
    tint: "rose",
  },
  {
    id: "ai",
    icon: Sparkles,
    title: "Попитай Nutramilo",
    subtitle: "Задай въпрос с твои думи. Ние ще преведем медицината.",
    cta: "Попитай",
    path: "/dashboard/analyses?tab=nutramilo_ai",
    tint: "pink",
  },
];

const TINT = {
  amber: {
    iconBg: "bg-amber-100",
    iconText: "text-amber-700",
    border: "hover:border-amber-300",
  },
  violet: {
    iconBg: "bg-violet-100",
    iconText: "text-violet-700",
    border: "hover:border-violet-300",
  },
  sky: {
    iconBg: "bg-sky-100",
    iconText: "text-sky-700",
    border: "hover:border-sky-300",
  },
  indigo: {
    iconBg: "bg-indigo-100",
    iconText: "text-indigo-700",
    border: "hover:border-indigo-300",
  },
  rose: {
    iconBg: "bg-rose-100",
    iconText: "text-rose-700",
    border: "hover:border-rose-300",
  },
  pink: {
    iconBg: "bg-pink-100",
    iconText: "text-pink-700",
    border: "hover:border-pink-300",
  },
};

export default function StarterTilesGrid() {
  const navigate = useNavigate();
  const { user, API } = useAuth();
  const [activity, setActivity] = useState({ hasMealsToday: null });

  // iter565 — Single light-weight call to detect whether the user has logged
  // any meal today. We use this to drive amber/rose urgency hints on
  // breakfast/water/sleep tiles. Silent failure → no urgency, no nag.
  useEffect(() => {
    if (!user) return;
    const token =
      localStorage.getItem("nutramilo_access_token") ||
      sessionStorage.getItem("nutramilo_access_token");
    if (!token) return;
    let cancelled = false;
    axios
      .get(`${API}/meals/today`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => {
        if (cancelled) return;
        const meals = r.data?.meals || r.data?.items || r.data || [];
        const count = Array.isArray(meals) ? meals.length : 0;
        setActivity({ hasMealsToday: count > 0 });
      })
      .catch(() => {
        // silent — no urgency overlay if we cannot check
      });
    return () => {
      cancelled = true;
    };
  }, [user, API]);

  // Resolve urgency per tile based on time-of-day + missing actions.
  // The function returns one of: "critical" | "warning" | "neutral".
  const urgencyFor = (tileId) => {
    const hour = new Date().getHours();
    const evening = hour >= 18;
    const noMeals = activity.hasMealsToday === false;
    // Only meals-driven tiles get smart cues for now. Other tiles stay neutral
    // until we have per-domain telemetry (sleep, water, labs are Phase 6+).
    if (tileId === "breakfast" && noMeals) {
      return evening ? "critical" : "warning";
    }
    return "neutral";
  };

  return (
    <section
      data-testid="starter-tiles-grid"
      aria-label="Бързи действия"
      className="mt-6"
    >
      <h2 className="text-base md:text-lg font-bold text-stone-900 mb-3">
        Какво да направиш сега
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {TILES.map((t) => {
          const Icon = t.icon;
          const tint = TINT[t.tint];
          const urgency = urgencyFor(t.id);
          return (
            <UrgencyBorder
              key={t.id}
              level={urgency}
              testId={`starter-tile-urgency-${t.id}`}
            >
              <button
                type="button"
                onClick={() => navigate(t.path)}
                data-testid={`starter-tile-${t.id}`}
                data-tile-urgency={urgency}
                className={`group block w-full text-left p-5 rounded-2xl bg-white border border-stone-200 ${tint.border} transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-pink-200`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${tint.iconBg}`}
                  >
                    <Icon className={`w-6 h-6 ${tint.iconText}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-base font-bold text-stone-900 leading-tight">
                      {t.title}
                    </div>
                    <p className="mt-1 text-sm text-stone-600 leading-relaxed">
                      {t.subtitle}
                    </p>
                  </div>
                </div>
                <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-stone-700 group-hover:text-stone-900 transition-colors">
                  {t.cta}
                  <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                </div>
              </button>
            </UrgencyBorder>
          );
        })}
      </div>
    </section>
  );
}
